import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Crown, Check, ArrowRight } from "lucide-react";
import { fetchFeaturedFree } from "@/lib/api";
import { useConfig } from "@/hooks/useConfig";
import { useAuth } from "@/context/AuthContext";
import PropStatCard from "@/components/prop/PropStatCard";
import SectionHeader from "@/components/SectionHeader";
import { TimezoneBar } from "@/components/TimezoneBar";
import { Skeleton } from "@/components/ui/skeleton";

const PERKS = ["Every VIP player-prop card with full statistical breakdown", "Multi-leg combinations & game picks", "Private Discord community access", "Permanent, public results ledger"];

export function JoinVipPanel() {
  const { config } = useConfig();
  const { isVip } = useAuth();
  if (isVip) return null;
  const url = config?.whop?.vip_checkout_url || "/#pricing";
  const monthly = config?.whop?.plans?.find((p) => p.key === "monthly");
  const yearly = config?.whop?.plans?.find((p) => p.key === "yearly");
  const price = monthly?.price ?? config?.whop?.pricing?.vip_monthly;
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[#F59E0B]/40 bg-[#141A26] p-6 sm:p-8" data-testid="featured-join-vip">
      <div className="pointer-events-none absolute -left-20 -bottom-20 h-56 w-56 rounded-full bg-[#F59E0B]/10 blur-3xl" />
      <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-xl">
          <p className="eyebrow !text-[#F59E0B]">Want more than one?</p>
          <h3 className="mt-2 font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">Unlock every VIP pick with the same statistical depth</h3>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">{PERKS.map((p) => <li key={p} className="flex items-start gap-2 text-sm text-slate-300"><Check size={15} className="mt-0.5 shrink-0 text-[#00E676]" />{p}</li>)}</ul>
        </div>
        <div className="shrink-0 text-center lg:text-right">
          {price && <p className="font-display text-3xl font-extrabold text-white">${price}<span className="font-mono text-sm text-slate-400">/mo</span>{yearly?.price ? <span className="block font-mono text-xs text-[#00E676]">or ${yearly.price}/yr · save {yearly.save_pct}%</span> : null}</p>}
          {config?.whop && !config.whop.sales_open && <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-[#F59E0B]" data-testid="featured-launch-note">Subscriptions open 19 Nov 2026</p>}
          <a href={url} target={url.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid="featured-join-vip-btn" className="btn-primary mt-3 !px-6 !py-3"><Crown size={15} /> {url.startsWith("http") ? "Join VIP" : "See VIP pricing"}</a>
          <Link to="/picks" className="mt-3 block font-mono text-[11px] uppercase tracking-wider text-slate-400 hover:text-[#00E676]" data-testid="featured-view-all">See all picks <ArrowRight size={11} className="inline" /></Link>
        </div>
      </div>
    </div>
  );
}

export function FeaturedFreePick({ compact = false }) {
  const { data, isLoading } = useQuery({ queryKey: ["featured-free"], queryFn: fetchFeaturedFree, staleTime: 5 * 60_000 });
  if (!isLoading && !data?.pick) return null;
  const inner = isLoading ? <Skeleton className="h-[520px] rounded-xl bg-[#141A26]" /> : (
    <div className="space-y-6">
      <PropStatCard pick={data.pick} stats={data.stats} />
      <JoinVipPanel />
    </div>
  );
  if (compact) return <div data-testid="featured-free-section">{inner}</div>;
  return (
    <section className="border-b border-[#1E2738] bg-[#0B0E14]" data-testid="featured-free-section">
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 sm:py-24 lg:px-8">
        <SectionHeader eyebrow="Featured Free Pick" title="Today's Free Statistical Breakdown" sub="Real game-by-game numbers against the line, plus the owner's full written reasoning — open to everyone." testid="featured-free-header" />
        <div className="mb-6"><TimezoneBar /></div>
        {inner}
      </div>
    </section>
  );
}

export default FeaturedFreePick;
